import type { UserRole } from './user.types'

/**
 * Rolové skupiny a brány zápisu — JEDEN zdroj pravdy pre API (vynucuje, cez
 * `apps/api/src/common/rbac/scope.util.ts`), admin aj mobil (len zobrazujú).
 * Čisté funkcie bez Prisma/Nest. Rola je `string`, lebo admin aj mobil ju majú
 * netypovanú (`/auth/me`, dekódovaný JWT); neznáma rola nepatrí do žiadnej skupiny.
 */

/** OWNER+ — celý tenant. */
export const OWNER_PLUS_ROLES = ['SUPER_ADMIN', 'OWNER'] as const satisfies readonly UserRole[]

/** Manažér+ — vidí aj cudzie záznamy (API `isManagerOrAbove`); BRANCH_MANAGER v rámci pobočky. */
export const MANAGER_PLUS_ROLES = ['SUPER_ADMIN', 'OWNER', 'BRANCH_MANAGER'] as const satisfies readonly UserRole[]

/**
 * Zamestnanci kancelárie (ASSISTANT+ v hierarchii, API `@Roles('ASSISTANT')` / `STAFF_ROLES`
 * v controlleroch) — všetci okrem TIPER a LAWYER, ktorí majú vlastné obmedzené rozhranie.
 */
export const STAFF_ROLES = ['SUPER_ADMIN', 'OWNER', 'BRANCH_MANAGER', 'BROKER', 'ASSISTANT'] as const satisfies readonly UserRole[]

/**
 * Roly, ktoré môžu byť maklérom nehnuteľnosti (`Property.agentId`): ponuka „Maklér",
 * filter makléra, predvyplnenie pri vytváraní, covery titulnej fotky. ASSISTANT
 * a SUPER_ADMIN maklérmi nie sú (SUPER_ADMIN pod prepnutým tenantom v tenante ani neexistuje).
 */
export const AGENT_ROLES = ['OWNER', 'BRANCH_MANAGER', 'BROKER'] as const satisfies readonly UserRole[]

/**
 * Roly, ktoré spravujú aj cudzie nehnuteľnosti — vidia ich vždy, nezávisle od
 * `propertyVisibility` (API `propertyVisibilityScope`: OWNER+ celý tenant,
 * BRANCH_MANAGER/ASSISTANT svoju pobočku), a smú vybrať/priradiť makléra.
 */
export const PROPERTY_TEAM_ROLES = ['SUPER_ADMIN', 'OWNER', 'BRANCH_MANAGER', 'ASSISTANT'] as const satisfies readonly UserRole[]

const hasRole = (roles: readonly string[], role: string | null | undefined): boolean =>
  !!role && roles.includes(role)

export const isOwnerPlus = (role: string | null | undefined): boolean => hasRole(OWNER_PLUS_ROLES, role)
export const isManagerPlus = (role: string | null | undefined): boolean => hasRole(MANAGER_PLUS_ROLES, role)
export const isStaffRole = (role: string | null | undefined): boolean => hasRole(STAFF_ROLES, role)
export const isAgentRole = (role: string | null | undefined): boolean => hasRole(AGENT_ROLES, role)
export const isPropertyTeamRole = (role: string | null | undefined): boolean => hasRole(PROPERTY_TEAM_ROLES, role)

/**
 * Môže byť agentom osoby (`contact_agents`) — zamestnanec kancelárie, nikdy TIPER (vidí svoje tipy cez
 * `tiperId`) ani LAWYER. Aktívny účet a tenant osoby stráži API pri zápise.
 */
export function canBeContactAgent(role: string | null | undefined): boolean {
  return isStaffRole(role)
}

/** Používateľ tak, ako ho nesie JWT (`sub` = id). Admin mapuje `AuthUser.id` → `sub`. */
export interface RbacUser {
  sub: string
  role: string
  branchId?: string | null
}

export interface RbacProperty {
  agentId?: string | null
  branchId?: string | null
}

/**
 * Filter pobočky (zoznam nehnuteľností, pipeline) má zmysel len tomu, kto vidí viac
 * pobočiek: OWNER+ alebo BRANCH_MANAGER bez priradenej pobočky (celý tenant).
 * Pobočkový manažér/asistent/maklér sú scopovaní — dropdown by dával prázdne výsledky.
 */
export function canUseBranchFilter(user: Pick<RbacUser, 'role' | 'branchId'> | null | undefined): boolean {
  if (!user) return false
  return isOwnerPlus(user.role) || (user.role === 'BRANCH_MANAGER' && !user.branchId)
}

/**
 * Vidí používateľ v zozname nehnuteľností aj cudzie inzeráty? Zrkadlo API
 * `propertyVisibilityScope`: tím (OWNER+, BRANCH_MANAGER, ASSISTANT) vždy; BROKER podľa
 * `tenant.settings.propertyVisibility` — `tenant` celý tenant, `branch` kolegov z pobočky
 * (len ak pobočku má), inak (`own`, chýbajúce nastavenie) len vlastné. Podľa toho sa
 * zobrazuje „Len moje" a v mobile filter makléra (filter obchodov je `isManagerPlus`).
 */
export function canSeeOthersProperties(
  user: Pick<RbacUser, 'role' | 'branchId'> | null | undefined,
  propertyVisibility: unknown,
): boolean {
  if (!user) return false
  if (isPropertyTeamRole(user.role)) return true
  if (user.role !== 'BROKER') return false
  return propertyVisibility === 'tenant' || (propertyVisibility === 'branch' && !!user.branchId)
}

/**
 * Write gate pre nehnuteľnosť a na ňu naviazané zdroje (médiá, publikácia, archív,
 * provízia, vlastníci): OWNER+ → všetko v tenante; BRANCH_MANAGER → len vlastná pobočka
 * (write ⊆ read, bez pobočky celý tenant); inak vlastná (`agentId`); ASSISTANT navyše
 * čokoľvek vo svojej pobočke. API ním vynucuje zápis, admin a mobil podľa neho
 * zobrazujú formulár len na čítanie.
 */
export function canWriteProperty(
  user: RbacUser | null | undefined,
  property: RbacProperty | null | undefined,
): boolean {
  if (!user?.role || !property) return false
  if (isOwnerPlus(user.role)) return true
  if (user.role === 'BRANCH_MANAGER') return !user.branchId || property.branchId === user.branchId
  if (property.agentId === user.sub) return true
  return user.role === 'ASSISTANT' && !!user.branchId && property.branchId === user.branchId
}

/**
 * Úprava, zmena stavu a zmazanie udalosti kalendára: organizátor (`agent`) alebo
 * manažér+ — zrkadlo API `ownAgentScope`. Pozvaný účastník udalosť len číta.
 */
export function canWriteEvent(
  user: Pick<RbacUser, 'sub' | 'role'> | null | undefined,
  event: { agent?: { id: string } | null },
): boolean {
  if (!user) return false
  return isManagerPlus(user.role) || event.agent?.id === user.sub
}

/**
 * Poznámku kontaktu, zákazníka, obchodu aj nehnuteľnosti upraví a zmaže jej autor alebo
 * manažér+ (API `NotesService` vynucuje, admin a mobil podľa neho skrývajú úpravy).
 * Zapisovať poznámky smú len zamestnanci — tiper a právnik ich len čítajú.
 */
export function canModifyNote(
  user: Pick<RbacUser, 'sub' | 'role'> | null | undefined,
  note: { authorId?: string | null; author?: { id: string } | null },
): boolean {
  if (!user || !isStaffRole(user.role)) return false
  return isManagerPlus(user.role) || (note.authorId ?? note.author?.id) === user.sub
}
